// hooks/use-download.ts
import { useLocalStorageState } from 'ahooks';
import { useCallback, useState } from 'react';
import { isEnterpriseDistribution, SITE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { CreationInfo } from '@/app/types';
import { useInterstitialAd } from './use-interstitial-ad';

/**
 * 水印类型定义
 *
 * none: 无水印
 * single: 单水印
 */
export type WatermarkType = 'none' | 'single';

/**
 * 文件类型
 */
export type CreationType = CreationInfo['type'] | undefined;

/**
 * 下载资源配置
 */
export interface DownloadResourceConfig {
  /** 文件类型 */
  creationType: CreationType;
  /** 创作ID */
  creationId: string;
  /** 原始 URI / 音频 URI */
  originalUri: string;
  /** 无水印 URI */
  nomarkUri?: string;
  /** 文件名 */
  fileName?: string;
  /** 是否有版权限制（有版权的视频必须带水印） */
  hasCopyright?: boolean;
  /**
   * 为单次下载指定是否带水印，优先级高于用户全局偏好（如导出成片按弹窗选择）。
   * true = 带水印资源，false = 无水印（在允许时使用 nomarkUri）。
   */
  withWatermarkOverride?: boolean;
}

const WATERMARK_STORAGE_KEY = 'VIDU_WITH_WATERMARK';

/**
 * 下载管理 Hook
 *
 * 统一管理下载相关的所有逻辑：
 * - 水印状态管理
 * - 获取下载 URL
 * - 水印确认弹窗
 * - 下载付费提醒
 *
 * 水印映射关系：
 *
 * 【视频/图片】
 * 国内站：
 * - 付费用户 withWatermark = true  → 无水印
 * - 免费用户 withWatermark = false → 带水印
 *
 * 海外站：看用户选择
 * - withWatermark = true  → 单水印
 * - withWatermark = false → 无水印
 *
 * 【音频】
 * 国内站：始终带水印（无论用户选择）
 * 海外站：始终不带水印（无论用户选择）
 *
 * @param isFree 是否免费用户（免费用户强制带水印）
 */
export function useDownloadUrl() {
  const [loading, setLoading] = useState<boolean>(false);
  const userInfo = useStore((state) => state.userInfo);
  const { markWatermarkInterstitialShown, canShowWatermarkInterstitialToday } = useInterstitialAd();

  // 水印状态管理
  // 免费用户默认带水印，付费用户默认不带水印
  const [withWatermark, setWithWatermark] = useLocalStorageState<boolean>(WATERMARK_STORAGE_KEY, {
    defaultValue: userInfo?.subs_plan === 'free',
    listenStorageChange: true,
  });

  /**
   * 最终水印状态计算：
   * 免费用户 => 带水印
   * 付费用户 => 看用户选择
   */
  const isWithWatermark = userInfo?.subs_plan === 'free' ? true : (withWatermark ?? false);

  /**
   * 获取实际的水印类型
   * @param creationType 文件类型
   * @param hasCopyright 是否有版权限制
   */
  const getWatermarkType = useCallback(
    (creationType: CreationType, hasCopyRight?: boolean, withWatermarkOverride?: boolean): WatermarkType => {
      const preference = withWatermarkOverride !== undefined ? withWatermarkOverride : isWithWatermark;
      const finalWithWatermark = hasCopyRight ? true : preference;

      // 音频特殊处理
      if (creationType === 'audio') {
        return SITE === 'china' ? 'single' : 'none';
      }
      return finalWithWatermark ? 'single' : 'none';
    },
    [isWithWatermark],
  );

  /**
   * 判断是否需要前端添加音频水印
   */
  const needsAudioWatermark = useCallback((creationType: CreationType): boolean => {
    return creationType === 'audio' && SITE === 'china' && !isEnterpriseDistribution;
  }, []);

  /**
   * 批量获取下载 URL
   *
   * 注意：单个文件下载也使用此方法，传入单元素数组即可
   *
   * @param configs 下载资源配置数组
   * @returns Promise<下载 URL 数组>
   */
  const getDownloadUrls = async (
    configs: DownloadResourceConfig[],
  ): Promise<Array<{ url: string; config: DownloadResourceConfig }>> => {
    const interstitialWatermark =
      configs.find((c) => c.withWatermarkOverride !== undefined)?.withWatermarkOverride ?? isWithWatermark;
    markWatermarkInterstitialShown(interstitialWatermark);

    setLoading(true);

    // 分类处理，能直接获取 URL 的先获取
    const results: Array<{ url: string; config: DownloadResourceConfig }> = [];
    const needsAudioWatermarkConfigs: DownloadResourceConfig[] = [];

    configs.forEach((config) => {
      const { creationType, originalUri, nomarkUri, hasCopyright, withWatermarkOverride } = config;

      // 计算水印类型，考虑版权限制（如果视频有版权限制，必须带水印）
      const watermarkType = getWatermarkType(creationType, hasCopyright, withWatermarkOverride);

      // 判断是否需要音频水印
      if (needsAudioWatermark(creationType)) {
        needsAudioWatermarkConfigs.push(config);
      } else {
        // 可以直接获取 URL 的情况
        let url = originalUri;
        // 无水印
        if (watermarkType === 'none' && nomarkUri) {
          url = nomarkUri;
        }
        results.push({ url, config });
      }
    });

    await Promise.all([
      // 暂时跳过前端音频加水印，保留音频分支结构，后续可直接恢复处理逻辑
      (async () => {
        if (needsAudioWatermarkConfigs.length === 0) return;

        const audioResults = await Promise.all(
          needsAudioWatermarkConfigs.map(async (config) => ({
            url: config.originalUri,
            config,
          })),
        );

        audioResults.forEach((result) => {
          results.push(result);
        });
      })(),
    ]);
    setLoading(false);
    return results;
  };

  return {
    withWatermark: isWithWatermark,
    setWithWatermark,
    loading: loading,
    getDownloadUrls,
    canShowWatermarkInterstitialToday,
  };
}
